import React, { useEffect, useState, useRef } from 'react';
import { useCall } from '../../context/CallContext';
import JGSignalStrength from '../jgSignalStrength/JGSignalStrength';

export default function CallOverlay() {
  const {
    active,
    remoteStream,
    localStream,
    minimized,
    minimize,
    restore,
    endCall,
    connectionState,
    peerStats,
  } = useCall();

  const [micEnabled, setMicEnabled] = useState(true);
  const [camEnabled, setCamEnabled] = useState(true);
  const [debugHud, setDebugHud] = useState(false);
  const localStreamRef = useRef(null);

  const stats = peerStats?.summary || peerStats || {};
  const bitrateRaw =
    stats.bitrateKbps ?? stats.bitrateKbpsDown ?? stats.bitrateKbpsUp ?? 0;
  const bitrateKbps = Math.round(bitrateRaw);

  const inboundBytesAudio =
    stats.bytesRecvAudio ??
    stats.bytesReceivedAudio ??
    stats.audioBytesRecv ??
    0;
  const inboundPacketsAudio =
    stats.packetsRecvAudio ??
    stats.packetsReceivedAudio ??
    stats.audioPacketsRecv ??
    0;
  const inboundBytesVideo =
    stats.bytesRecvVideo ??
    stats.bytesReceivedVideo ??
    stats.videoBytesRecv ??
    0;
  const inboundPacketsVideo =
    stats.packetsRecvVideo ??
    stats.packetsReceivedVideo ??
    stats.videoPacketsRecv ??
    0;
  const selectedPair = stats.selectedPair || peerStats?.selectedPair || null;

  function computeUiSignalStrength(kbps) {
    if (kbps >= 500) return 'excellent';
    if (kbps >= 200) return 'good';
    if (kbps >= 50) return 'fair';
    return 'poor';
  }

  const uiSignalQuality = computeUiSignalStrength(bitrateKbps);

  useEffect(() => {
    localStreamRef.current = localStream;
    if (!localStream) return;

    try {
      localStream.getAudioTracks().forEach((t) => (t.enabled = micEnabled));
      localStream.getVideoTracks().forEach((t) => (t.enabled = camEnabled));
    } catch (e) {}
  }, [localStream, micEnabled, camEnabled]);

  useEffect(() => {
    const handler = (e) => {
      if (e.shiftKey && e.key.toLowerCase() === 'd') {
        setDebugHud((v) => !v);
      }
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, []);

  if (!active) return null;

  const isVideoCall = !!active.isVideo;
  const humanState =
    connectionState === 'connected'
      ? 'Connected'
      : connectionState === 'dialing'
      ? 'Dialing'
      : connectionState === 'ringing'
      ? 'Ringing'
      : connectionState === 'media-stalled'
      ? 'Connection stalled'
      : connectionState === 'failed'
      ? 'No media detected'
      : connectionState === 'ended'
      ? 'Ended'
      : 'Connecting...';
  const isMediaStalled =
    connectionState === 'media-stalled' || connectionState === 'failed';
  const title = active.peerHandle
    ? `Call with @${active.peerHandle}`
    : `${isVideoCall ? 'Video' : 'Audio'} call`;

  const handleToggleMic = () => {
    const next = !micEnabled;
    setMicEnabled(next);
    try {
      localStreamRef.current
        ?.getAudioTracks()
        .forEach((t) => (t.enabled = next));
    } catch (_) {}
  };

  const handleToggleCam = () => {
    const next = !camEnabled;
    setCamEnabled(next);
    try {
      localStreamRef.current
        ?.getVideoTracks()
        .forEach((t) => (t.enabled = next));
    } catch (_) {}
  };

  const handleMinimize = () => minimize?.();
  const handleRestore = () => restore?.();
  const handleEndCall = () => endCall({ reason: 'local-ended' });
  const persistentSinkAudible = !isVideoCall || minimized;
  const remoteAudioSink = (
    <RemoteAudioSink
      stream={remoteStream}
      muted={!persistentSinkAudible}
      active={!!active}
    />
  );

  if (minimized) {
    return (
      <>
        {remoteAudioSink}
        <div style={miniWrap}>
          <span style={{ fontWeight: 700 }}>{title}</span>
          <div style={miniButtons}>
            <button
              onClick={handleToggleMic}
              style={pill(micEnabled ? '#238636' : '#b4232d')}
            >
              {micEnabled ? 'Mic On' : 'Mic Off'}
            </button>

            {isVideoCall && (
              <button
                onClick={handleToggleCam}
                style={pill(camEnabled ? '#238636' : '#b4232d')}
              >
                {camEnabled ? 'Cam On' : 'Cam Off'}
              </button>
            )}

            <button onClick={handleRestore} style={pill('transparent', true)}>
              Restore
            </button>

            <button onClick={handleEndCall} style={pill('#b4232d')}>
              End
            </button>
          </div>
        </div>
      </>
    );
  }

  return (
    <>
      {remoteAudioSink}
      <div style={overlayBg}>
        <div className="call-panel">
          <div style={panelHeader}>
            <div
              style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                gap: 8,
              }}
            >
              <strong>{title}</strong>

              <span
                style={{
                  fontSize: 11,
                  padding: '3px 8px',
                  borderRadius: 999,
                  background:
                    connectionState === 'connected'
                      ? 'rgba(34,197,94,0.15)'
                      : connectionState === 'connecting' ||
                        connectionState === 'answered' ||
                        connectionState === 'dialing'
                      ? 'rgba(59,130,246,0.18)'
                      : 'rgba(248,113,113,0.18)',
                  color:
                    connectionState === 'connected'
                      ? '#4ade80'
                      : connectionState === 'connecting' ||
                        connectionState === 'answered' ||
                        connectionState === 'dialing'
                      ? '#93c5fd'
                      : '#fecaca',
                  textTransform: 'uppercase',
                  letterSpacing: 0.06,
                  fontWeight: 700,
                }}
              >
                {humanState}
              </span>
            </div>
            {isMediaStalled ? (
              <div
                role="status"
                data-testid="call-media-stalled"
                style={stallNotice}
              >
                Connection stalled - trying to restore media.
                <br />
                No media detected.
              </div>
            ) : null}
          </div>

          <div
            style={{
              margin: '8px 12px 4px',
              padding: '6px 10px',
              borderRadius: 10,
              background: 'rgba(255,255,255,0.04)',
              textAlign: 'left',
              fontSize: 11,
              fontFamily: 'monospace',
              lineHeight: 1.4,
            }}
          >
            <div style={{ fontWeight: 600, marginBottom: 2 }}>RTC</div>
            <div>State: {humanState}</div>
            <div>B (audio): {inboundBytesAudio}</div>
            <div>P (audio): {inboundPacketsAudio}</div>
            <div>B (video): {inboundBytesVideo}</div>
            <div>P (video): {inboundPacketsVideo}</div>
            <div style={{ marginTop: 4 }}>Br: {bitrateKbps} kbps</div>
            <div>Q: {uiSignalQuality}</div>

            <div style={{ marginTop: 6 }}>
              <JGSignalStrength bitrateKbps={bitrateKbps} />
            </div>

            {debugHud && (
              <div style={{ marginTop: 6, opacity: 0.8 }}>
                <div>Updated: {stats.updatedAt || '-'}</div>
                <div>
                  Pkts/s:{' '}
                  {stats.packetsPerSecondAudio ?? stats.packetsPerSecond ?? 0}
                </div>

                {selectedPair && (
                  <div style={{ marginTop: 6 }}>
                    <div>Selected Pair:</div>
                    <div style={{ fontSize: 10 }}>
                      id: {selectedPair.id} | state: {selectedPair.state}
                    </div>
                    <div style={{ fontSize: 10 }}>
                      nominated: {String(selectedPair.nominated)}
                      {' | '}selected: {String(selectedPair.selected)}
                    </div>
                    <div style={{ fontSize: 10 }}>
                      bytesSent: {selectedPair.bytesSent ?? 0}
                      {' | '}bytesRecv: {selectedPair.bytesReceived ?? 0}
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>

          <div className="video-wrapper">
            <MediaView
              label="Remote"
              stream={remoteStream}
              isVideo={isVideoCall}
              audible={isVideoCall}
            />
            <MediaView
              label="You"
              stream={camEnabled ? localStream : null}
              isVideo={isVideoCall}
              mirror
              audible={false}
            />
          </div>

          <div className="button-wrapper">
            <button className="btn mic" onClick={handleToggleMic}>
              {micEnabled ? 'Mute' : 'Unmute'}
            </button>

            {isVideoCall && (
              <button className="btn cam" onClick={handleToggleCam}>
                {camEnabled ? 'Video Off' : 'Video On'}
              </button>
            )}

            <button className="btn outline" onClick={handleMinimize}>
              Minimize
            </button>

            <button className="btn end" onClick={handleEndCall}>
              End Call
            </button>
          </div>
        </div>

        <style>{`
          .call-panel {
            width: min(92vw, 920px);
            border-radius: 16px;
            background: #0d1117;
            color: #fff;
            border: 1px solid rgba(255,255,255,0.12);
            box-shadow:
              0 20px 60px rgba(0, 0, 0, 0.7),
              0 10px 24px rgba(0, 0, 0, 0.4);
            max-height: 100vh;
            overflow-y: auto;
            padding-bottom: env(safe-area-inset-bottom, 24px);
            display: flex;
            flex-direction: column;
          }

          .video-wrapper {
            display: grid;
            grid-template-columns: 1fr 1fr;
            gap: 12px;
            padding: 12px;
          }

          .button-wrapper {
            display: flex;
            flex-wrap: wrap;
            justify-content: center;
            gap: 10px;
            padding: 16px;
            border-top: 1px solid rgba(255,255,255,0.08);
          }

          .btn {
            flex: 1;
            min-width: 120px;
            padding: 12px 14px;
            border-radius: 12px;
            font-weight: 700;
            font-size: 15px;
            cursor: pointer;
            border: none;
          }
          .btn.mic,
          .btn.cam {
            background: #238636;
            color: #fff;
          }
          .btn.outline {
            background: transparent;
            border: 1px solid rgba(255,255,255,0.15);
            color: #9fb4ff;
          }
          .btn.end {
            background: #b4232d;
            color: #fff;
          }

          @media (max-width: 768px), (orientation: portrait) {
            .call-panel {
              width: 96vw;
              max-height: 100vh;
              overflow-y: auto;
            }
            .video-wrapper {
              grid-template-columns: 1fr;
            }
          }
        `}</style>
      </div>
    </>
  );
}

const overlayBg = {
  position: 'fixed',
  inset: 0,
  zIndex: 2147483647,
  background: 'rgba(0,0,0,0.75)',
  display: 'grid',
  placeItems: 'center',
  fontFamily: 'system-ui,-apple-system,Segoe UI,Roboto,Helvetica,Arial',
};

const panelHeader = {
  padding: 12,
  borderBottom: '1px solid rgba(255,255,255,0.08)',
};

const miniWrap = {
  position: 'fixed',
  bottom: 16,
  left: 16,
  right: 16,
  zIndex: 2147483647,
  background: '#0d1117',
  color: '#fff',
  border: '1px solid rgba(255,255,255,0.1)',
  borderRadius: 14,
  boxShadow: '0 10px 30px rgba(0,0,0,0.6)',
  display: 'flex',
  flexDirection: 'column',
  padding: 10,
  maxWidth: '100vw',
  overflowX: 'hidden',
};

const miniButtons = {
  display: 'flex',
  flexWrap: 'wrap',
  justifyContent: 'center',
  gap: 8,
  marginTop: 6,
};

const stallNotice = {
  marginTop: 8,
  padding: '8px 10px',
  borderRadius: 10,
  background: 'rgba(248,113,113,0.14)',
  border: '1px solid rgba(248,113,113,0.28)',
  color: '#fecaca',
  fontSize: 12,
  lineHeight: 1.35,
};

const visuallyHiddenMedia = {
  position: 'fixed',
  width: 1,
  height: 1,
  opacity: 0,
  pointerEvents: 'none',
  left: -9999,
  top: -9999,
};

function pill(bg, outline = false) {
  return {
    padding: '6px 10px',
    borderRadius: 999,
    background: bg,
    color: bg === 'transparent' ? '#9fb4ff' : '#fff',
    border: outline ? '1px solid rgba(255,255,255,0.15)' : 'none',
    cursor: 'pointer',
    fontWeight: 700,
    flexShrink: 0,
  };
}

function RemoteAudioSink({ stream, muted, active }) {
  const ref = useRef(null);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;

    if (!active || !stream) {
      el.srcObject = null;
      return;
    }

    try {
      el.srcObject = stream;
      const p = el.play?.();
      if (p && typeof p.catch === 'function') p.catch(() => {});
    } catch (_) {}
  }, [active, stream]);

  if (!active || !stream) return null;

  return (
    <audio
      ref={ref}
      autoPlay
      muted={muted}
      aria-hidden="true"
      tabIndex={-1}
      data-testid="persistent-remote-audio-sink"
      style={visuallyHiddenMedia}
    />
  );
}

function MediaView({ label, stream, isVideo, mirror = false, audible = true }) {
  const ref = useRef(null);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;

    if (!stream) {
      el.srcObject = null;
      return;
    }

    try {
      el.srcObject = stream;
      const p = el.play?.();
      if (p && typeof p.catch === 'function') p.catch(() => {});
    } catch (_) {}
  }, [stream]);

  const testId = `${label.toLowerCase()}-${isVideo ? 'video' : 'audio'}-media`;

  return (
    <div style={{ flex: 1, minWidth: 220 }}>
      <div style={{ fontSize: 12, opacity: 0.8, marginBottom: 6 }}>{label}</div>

      {isVideo ? (
        <video
          ref={ref}
          playsInline
          muted={!audible || label === 'You'}
          data-testid={testId}
          style={{
            width: '100%',
            height: 260,
            objectFit: 'cover',
            borderRadius: 12,
            transform: mirror ? 'scaleX(-1)' : 'none',
            background: '#111',
            border: '1px solid rgba(255,255,255,0.06)',
          }}
        />
      ) : (
        <>
          <audio
            ref={ref}
            autoPlay
            muted={!audible || label === 'You'}
            data-testid={testId}
            style={{ display: 'none' }}
          />
          <div
            style={{
              height: 120,
              borderRadius: 12,
              background:
                'linear-gradient(180deg, rgba(255,255,255,0.06), rgba(255,255,255,0.02))',
              border: '1px solid rgba(255,255,255,0.06)',
              display: 'grid',
              placeItems: 'center',
              fontSize: 14,
              color: '#bbb',
            }}
          >
            Audio connected
          </div>
        </>
      )}
    </div>
  );
}
